import JSBI from 'jsbi';
import {
  STORAGE_TO_REGISTER_WITH_MFT,
  ZERO,
  DEFAULT_GAS,
} from '../../../constants';
import { UserTokensMap } from '../../../types';
import { Percentage } from '../../../utils';
import { checkIsMultiHopOnSameAMM } from '../../../utils/route';
import { Amm, SwapMode } from '../amm/types';
import { SwapRoute, SwapStep, Route } from './types';

const EMPTY_ROUTE_RESPONSE: SwapRoute = {
  steps: [],
  inputMint: '',
  outputMint: '',
  amountIn: ZERO,
  amountOut: ZERO,
  minAmountOut: ZERO,
  swapMode: SwapMode.ExactIn,
  notEnoughLiquidity: true,
  priceImpact: 0,
  depositAndNetworkFee: ZERO,
  platformFee: {
    amount: ZERO,
    token: '',
    pct: 0,
  },
};

const createRouteFromExactIn = ({
  route,
  feeBps,
  swapMode,
  amountIn,
  slippage,
  inputMint,
  outputMint,
  userTokensMap,
}: {
  route: Route;
  feeBps: Percentage;
  swapMode: SwapMode;
  amountIn: JSBI;
  slippage: Percentage;
  inputMint: string;
  outputMint: string;
  userTokensMap: UserTokensMap;
}): SwapRoute => {
  const swapSteps: SwapStep[] = [];
  const isMultiHopOnSameAMM = checkIsMultiHopOnSameAMM(
    route.map((step) => step.amm)
  );

  let tempSwapAmountIn = amountIn;
  let index = 0;

  for (const { amm, inputToken, outputToken } of route) {
    if (!amm) {
      return EMPTY_ROUTE_RESPONSE;
    }

    const zeroSlippage = Percentage.fromSlippageNumber(0);
    const slippageToUse =
      index === 0 && isMultiHopOnSameAMM ? zeroSlippage : slippage;

    const {
      amountIn,
      amountOut,
      minAmountOut,
      feeAmount,
      feeMint,
      priceImpact,
      notEnoughLiquidity,
    } = amm.getQuote({
      inputMint: inputToken,
      outputMint: outputToken,
      amount: tempSwapAmountIn,
      swapMode,
      slippage: slippageToUse,
    });

    swapSteps.push({
      amm,
      inputMint: inputToken,
      outputMint: outputToken,
      amountIn,
      amountOut,
      minAmountOut,
      feeAmount,
      feeMint, // we take fee from pools from output
      priceImpact,
      notEnoughLiquidity,
    });

    tempSwapAmountIn = minAmountOut;
    index++;
  }

  const amountOut =
    swapSteps.length > 0 ? swapSteps[swapSteps.length - 1].amountOut : ZERO;

  const platformFeeAmount = feeBps.getFrom(amountOut);
  // update only ui value, so we don't reduce min value we want to receive and take part of it
  const amountOutLessPlatformFee = JSBI.subtract(amountOut, platformFeeAmount);

  const minAmountOut =
    swapSteps.length > 0 ? swapSteps[swapSteps.length - 1].minAmountOut : ZERO;

  const notEnoughLiquidity = swapSteps.some((step) => step.notEnoughLiquidity);

  const commonPriceImpact =
    1 -
    swapSteps.reduce((priceFactor, step) => {
      return priceFactor * (1 - step.priceImpact);
    }, 1);

  const createTokenAccountsFee = swapSteps.reduce((acc, step) => {
    const { existing } = userTokensMap.get(step.outputMint) || {
      existing: false,
    };

    if (!existing) {
      return JSBI.add(acc, JSBI.BigInt(STORAGE_TO_REGISTER_WITH_MFT));
    }

    return acc;
  }, ZERO);

  const transactionsFee = JSBI.multiply(
    JSBI.BigInt(DEFAULT_GAS),
    JSBI.BigInt(swapSteps.length)
  );
  const depositAndNetworkFee = JSBI.add(
    createTokenAccountsFee,
    transactionsFee
  );

  return {
    steps: swapSteps,
    amountIn,
    amountOut: amountOutLessPlatformFee,
    minAmountOut,
    inputMint,
    outputMint,
    swapMode,
    notEnoughLiquidity,
    priceImpact: commonPriceImpact,
    depositAndNetworkFee,
    platformFee: {
      amount: platformFeeAmount,
      token: outputMint,
      pct: +feeBps.numerator.toString() / 100,
    },
  };
};

const createRouteFromExactOut = ({
  route,
  feeBps,
  swapMode,
  amountOut,
  slippage,
  inputMint,
  outputMint,
  userTokensMap,
}: {
  route: Route;
  feeBps: Percentage;
  swapMode: SwapMode;
  amountOut: JSBI;
  slippage: Percentage;
  inputMint: string;
  outputMint: string;
  userTokensMap: UserTokensMap;
}): SwapRoute => {
  const swapSteps: SwapStep[] = [];
  const isMultiHopOnSameAMM = checkIsMultiHopOnSameAMM(
    route.map((step) => step.amm)
  );

  let tempSwapAmountOut = amountOut;
  let index = 0;

  const reversedRoute = route.reverse();

  for (const { amm, inputToken, outputToken } of reversedRoute) {
    if (!amm) {
      return EMPTY_ROUTE_RESPONSE;
    }

    const zeroSlippage = Percentage.fromSlippageNumber(0);
    const slippageToUse =
      index === 0 && isMultiHopOnSameAMM ? zeroSlippage : slippage;

    const {
      amountIn,
      amountOut,
      minAmountOut,
      feeAmount,
      feeMint,
      notEnoughLiquidity,
      priceImpact,
    } = amm.getQuote({
      inputMint: inputToken,
      outputMint: outputToken,
      amount: tempSwapAmountOut,
      swapMode,
      slippage: slippageToUse,
    });

    swapSteps.push({
      amm,
      inputMint: inputToken,
      outputMint: outputToken,
      amountIn,
      amountOut,
      minAmountOut,
      feeAmount,
      feeMint, // we take fee from pools from output
      priceImpact,
      notEnoughLiquidity,
    });

    tempSwapAmountOut = amountIn;
    index++;
  }

  // updating amount in to amount out with slippage
  // TODO: need to be discussed
  const reversedSteps = swapSteps
    .reverse()
    .reduce<SwapStep[]>((acc, step, index) => {
      if (index === 0) {
        acc.push(step);
        return acc;
      }

      const updatedStep = {
        ...step,
        amountIn: acc[acc.length - 1].minAmountOut,
      };

      acc.push(updatedStep);
      return acc;
    }, []);

  const amountIn = reversedSteps.length > 0 ? reversedSteps[0].amountIn : ZERO;

  // can be different from one in params due to stable pools
  // const totalAmountOut =
  //   reversedSteps.length > 0
  //     ? reversedSteps[reversedSteps.length - 1].amountOut
  //     : ZERO;

  const minAmountOut =
    reversedSteps.length > 0
      ? reversedSteps[reversedSteps.length - 1].minAmountOut
      : ZERO;

  const platformFeeAmount = feeBps.getFrom(amountIn);
  // update only ui value, so we don't reduce min value we want to receive and take part of it
  const amountInAddPlatformFee = JSBI.add(amountIn, platformFeeAmount);

  // update in steps too
  reversedSteps[0].amountIn = amountInAddPlatformFee;

  const notEnoughLiquidity = swapSteps.some((step) => step.notEnoughLiquidity);

  const commonPriceImpact =
    1 -
    swapSteps.reduce((priceFactor, step) => {
      return priceFactor * (1 - step.priceImpact);
    }, 1);

  const createTokenAccountsFee = reversedSteps.reduce((acc, step) => {
    const { existing } = userTokensMap.get(step.outputMint) || {
      existing: false,
    };
    if (!existing) {
      return JSBI.add(acc, JSBI.BigInt(STORAGE_TO_REGISTER_WITH_MFT));
    }

    return acc;
  }, ZERO);

  const transactionsFee = JSBI.multiply(
    JSBI.BigInt(DEFAULT_GAS),
    JSBI.BigInt(reversedSteps.length)
  );
  const depositAndNetworkFee = JSBI.add(
    createTokenAccountsFee,
    transactionsFee
  );

  return {
    steps: reversedSteps,
    amountIn: amountInAddPlatformFee,
    amountOut,
    minAmountOut,
    inputMint,
    outputMint,
    swapMode,
    notEnoughLiquidity,
    priceImpact: commonPriceImpact,
    depositAndNetworkFee,
    platformFee: {
      amount: platformFeeAmount,
      token: inputMint,
      pct: +feeBps.numerator.toString() / 100,
    },
  };
};

const getSwapRoutes = ({
  routes,
  amount,
  swapMode = SwapMode.ExactIn,
  inputMint,
  outputMint,
  feeBps,
  slippage,
  userTokensMap,
}: {
  routes: Route[];
  amount: JSBI;
  swapMode?: SwapMode;
  inputMint: string;
  outputMint: string;
  feeBps: Percentage;
  slippage: Percentage;
  userTokensMap: UserTokensMap;
}) => {
  if (routes.length === 0) {
    return [EMPTY_ROUTE_RESPONSE];
  }

  const filterRoute = (route: SwapRoute) =>
    JSBI.greaterThan(route.amountIn, ZERO) &&
    JSBI.greaterThan(route.amountOut, ZERO) &&
    // stable pools
    JSBI.greaterThan(route.steps[route.steps.length - 1].amountOut, ZERO) &&
    Math.abs(route.priceImpact) < 0.9 && // TODO: remove that when stable calculation will be fixed
    !route.notEnoughLiquidity;

  if (swapMode === SwapMode.ExactIn) {
    // console.time('calculate route exact in');
    const computedRoutes = routes.map((route) => {
      return createRouteFromExactIn({
        route,
        amountIn: amount,
        slippage,
        inputMint,
        outputMint,
        swapMode,
        feeBps,
        userTokensMap,
      });
    });

    // console.timeEnd('calculate route exact in');

    return computedRoutes
      .filter(filterRoute)
      .sort((routeA, routeB) =>
        JSBI.greaterThanOrEqual(routeB.amountOut, routeA.amountOut) ? 1 : -1
      );
  } else if (swapMode === SwapMode.ExactOut) {
    // console.time('calculate route exact out');

    const computedRoutes = routes.map((route) => {
      return createRouteFromExactOut({
        route,
        amountOut: amount,
        slippage,
        inputMint,
        outputMint,
        swapMode,
        feeBps,
        userTokensMap,
      });
    });

    // console.timeEnd('calculate route exact out');

    return computedRoutes
      .filter(filterRoute)
      .sort((routeA, routeB) =>
        JSBI.lessThanOrEqual(routeB.amountIn, routeA.amountIn) ? 1 : -1
      );
  }

  return [EMPTY_ROUTE_RESPONSE];
};

export { getSwapRoutes };
